import type { ApolloQueryResult } from '@apollo/client';
import { Tier1RowActions, Tier3RowActions, Tier4RowActions } from '@openx/constants/rowActions.ts';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers.ts';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type {
  GetAudiencesListQuery,
  GetAudiencesListQueryVariables,
} from '../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { EDITABLE_STATUSES } from '../constants.ts';
import { useArchiveAudiences } from './useArchiveAudiences.tsx';
import { useDuplicateAudience } from './useDuplicateAudience.tsx';
import { useShareAudience } from './useShareAudience.tsx';

type RefetchAudiencesList = (
  variables?: Partial<GetAudiencesListQueryVariables>
) => Promise<ApolloQueryResult<GetAudiencesListQuery>>;

export const useRowActions = (
  refetchList: RefetchAudiencesList,
  setSelectedAudiences: Dispatch<SetStateAction<GetAudiencesListQuery['audience']>>
) => {
  const { t } = useTranslation();
  const { openArchiveModal, isLoading: archiveLoading } = useArchiveAudiences();
  const { openShareModal, isLoading: shareLoading } = useShareAudience();
  const { openDuplicateModal, isLoading: duplicateLoading } = useDuplicateAudience(() => {
    refetchList();
  });
  const navigate = useNavigate();
  const [updatingAudienceId, setUpdatingAudienceId] = useState<string>('');

  const isLoading = useCallback(
    (audienceId: string) => (archiveLoading || duplicateLoading || shareLoading) && audienceId === updatingAudienceId,
    [archiveLoading, duplicateLoading, shareLoading, updatingAudienceId]
  );

  return useMemo(
    () =>
      sortMenuButtonItems<GetAudiencesListQuery['audience'][number]>({
        [Tier1RowActions.Details]: {
          label: t('Details'),
          onClick: ({ id }) => {
            navigate(`/audiences/${id}`);
          },
        },
        [Tier3RowActions.Edit]: {
          label: t('Edit'),
          onClick: ({ id }) => navigate(`/audiences/${id}/edit`),
          allowed: audience => (EDITABLE_STATUSES as readonly string[]).includes(audience.status),
          notAllowedMessage: t('This audience cannot be edited in its current status.'),
        },
        [Tier3RowActions.Delete]: {
          danger: true,
          label: t('Archive'),
          loading: audience => isLoading(audience.id),
          onClick: audience => {
            setUpdatingAudienceId(audience.id);
            openArchiveModal({
              audiences: [audience],
              onSuccess: () => {
                setSelectedAudiences(prev => prev.filter(selectedAudience => selectedAudience.id !== audience.id));
                refetchList();
              },
            });
          },
        },
        [Tier4RowActions.Duplicate]: {
          label: t('Duplicate'),
          loading: audience => isLoading(audience.id),
          onClick: audience => {
            setUpdatingAudienceId(audience.id);
            openDuplicateModal({ audience: audience });
          },
        },
        [Tier4RowActions.UseAsTemplate]: {
          label: t('Use as Template'),
          loading: audience => isLoading(audience.id),
          onClick: audience => {
            navigate('/audiences/create', {
              state: {
                audienceId: audience.id,
                useAsTemplate: true,
              },
            });
          },
        },
        [Tier4RowActions.ShareAudience]: {
          label: t('Share Audience'),
          loading: audience => isLoading(audience.id),
          onClick: audience => {
            setUpdatingAudienceId(audience.id);
            openShareModal({ audience: audience });
          },
        },
      }),
    [navigate, t, openArchiveModal, openDuplicateModal, isLoading, setSelectedAudiences, refetchList, openShareModal]
  );
};
